// Register.tsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { Button, Form, Input, Select } from 'antd';

// API 기본 URL 설정
//const API_BASE_URL = 'http://172.16.20.112:3000';
const API_BASE_URL = "https://rek-hybrid.co.kr";

// --- 인터페이스 정의 (백엔드 구조에 맞춰 정의) ---

interface Area {
  area_id: number;
  area_name: string;
}

interface Site {
  site_id: number;
  site_name: string;
}

interface User {
  id: string;
  name: string;
}

interface CreateGroupPayload {
    site_id: number;
    group_id: number;
    group_name: string;
}

interface CreateAccessPayload {
    user_id: string;
    site_id: number;
}


// 탭 목록 정의
const tabs = {
    USER: '유저 등록',
    AREA: '지역 등록',
    SITE: '사이트 등록',
    GROUP: '디바이스 그룹 등록',
    ACCESS: '유저 사이트 권한 등록',
};

type TabKey = keyof typeof tabs;

const { Option } = Select;

// ----------------------------------------------------

const Register: React.FC = () => {
    // 공통 상태
    const [activeTab, setActiveTab] = useState<TabKey>('USER');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    
    // 데이터 로딩 상태
    const [areas, setAreas] = useState<Area[]>([]);
    const [users, setUsers] = useState<User[]>([]);

    // 폼 입력 상태
    const [form] = Form.useForm();
    
    const [areaName, setAreaName] = useState(''); // 지역 등록 폼
    const [siteName, setSiteName] = useState(''); // 사이트 등록 폼
    const [location, setLocation] = useState(''); // 사이트 등록 폼
    const [groupId, setGroupId] = useState(''); // 그룹 등록 폼
    const [groupName, setGroupName] = useState(''); // 그룹 등록 폼

    // 선택 상태 (사이트 등록, 그룹 등록, 권한 등록 폼에 공통 사용)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
    const [sites, setSites] = useState<Site[]>([]); // 선택된 지역에 따른 사이트 목록
    const [selectedUserId, setSelectedUserId] = useState<string>(''); // 권한 등록 폼


    const navigate = useNavigate();

    const handleMainPage = () => {
        navigate('/main'); 
    };  

    // --- 1. 초기 데이터 로딩 (사용자, 지역 목록) ---

    const fetchInitialData = useCallback(async () => {
        try {
            // 지역 목록 로딩
            const areasResponse = await axios.get<Area[]>(`${API_BASE_URL}/api/user-site-access/areas`);
            setAreas(areasResponse.data);

            // 사용자 목록 로딩
            const usersResponse = await axios.get<User[]>(`${API_BASE_URL}/api/user-site-access/users`);
            setUsers(usersResponse.data);
            
            setError('');
        } catch (err) {
            console.error("Error fetching initial data:", err);
            setError('초기 데이터(사용자/지역 목록)를 불러오는 데 실패했습니다.');
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);


    // --- 2. 지역 선택 시 사이트 목록 로딩 ---

    const handleAreaChange = (value: number) => {
        const areaId = Number(value) || null;
        setSelectedAreaId(areaId); // 상태 업데이트

        // 폼에서 site_id 값 초기화
        form.setFieldsValue({ site_id: undefined });
        setSites([]); // 새로운 데이터 로딩 전 사이트 목록 초기화

        if (!areaId) {
            return;
        }

        // 💡 API 호출 로직 (기존 useEffect 내용 반영)
        const fetchSitesByArea = async () => {
            try {
                const response = await axios.get<Site[]>(
                    `${API_BASE_URL}/api/user-site-access/sites/by-area/${areaId}`
                );
                setSites(response.data);
            } catch (err) {
                console.error("Error fetching sites by area:", err);
                // 사용자에게 로딩 실패를 알리는 등의 피드백 추가 가능
            }
        };
        fetchSitesByArea();
    };

    useEffect(() => {
        if (!selectedAreaId) {
            setSites([]);
            setSelectedSiteId(null);
            return;
        }

        const fetchSitesByArea = async () => {
            setSites([]);
            setSelectedSiteId(null);
            try {
                const response = await axios.get<Site[]>(`${API_BASE_URL}/api/user-site-access/sites/by-area/${selectedAreaId}`);
                setSites(response.data);
            } catch (err) {
                console.error("Error fetching sites by area:", err);
                // 특정 에러를 보여주지 않고, 로딩 실패만 알림
            }
        };
        fetchSitesByArea();
    }, [selectedAreaId]);


    // --- 3. 폼 제출 핸들러 ---
    const handleRegionSubmit = async (values: {areaName: string;}) => {
        setMessage('');
        setError('');

        try {
            const payload = {
                area_name: values.areaName.trim()
            };
            // 사이트 등록 API 호출
            const response = await axios.post(`${API_BASE_URL}/api/areas`, payload);

            // 성공 메시지 처리
            setMessage(`지역 등록 성공: ${response.data.area_name}`);

            // 폼 필드 초기화: setSiteName, setLocation 대신 form.resetFields() 사용
            form.resetFields(); 
            await fetchInitialData(); // 지역 목록 갱신

        } catch (err) {
            const apiError = axios.isAxiosError(err) ? err.response?.data?.message : '네트워크 오류';
            setError(`지역 등록 실패: ${apiError}`);
        }
    };


    const handleSiteSubmit = async (values: { area_id: number; siteName: string; location?: string }) => {
        setMessage('');
        setError('');

        try {
            const payload = {
                area_id: values.area_id,
                site_name: values.siteName.trim(),
                // location 필드는 값이 없으면 null로 처리
                location: values.location?.trim() || null, 
            };
            
            // 사이트 등록 API 호출
            const response = await axios.post(`${API_BASE_URL}/api/sites`, payload);
            
            // 성공 메시지 처리
            setMessage(`사이트 등록 성공: ${response.data.site_name}`);
            
            // 폼 필드 초기화: setSiteName, setLocation 대신 form.resetFields() 사용
            form.resetFields(); 
            
        } catch (err) {
            const apiError = axios.isAxiosError(err) ? err.response?.data?.message : '네트워크 오류';
            setError(`사이트 등록 실패: ${apiError}`);
        }
    };    


    const handleGroupSubmit = async (values: { area_id: number; site_id: number; groupName: string }) => {
        setMessage('');
        setError('');

        // Ant Design Form이 이미 유효성 검사(required, pattern)를 완료했으므로
        // isNaN이나 trim() 검사는 불필요합니다.

        // payload 구조를 서버 요구 사항에 맞게 재구성합니다.
        const payload = {
            site_id: values.site_id,
            group_name: values.groupName.trim(), 
        };

        try {
            // 그룹 등록 API 호출
            const response = await axios.post(`${API_BASE_URL}/api/device-groups`, payload);
            
            setMessage(`그룹 등록 성공! Site ID: ${response.data.site_id}, 이름: ${response.data.group_name}`);
            
            // 폼 필드 초기화 (Ant Design Form에서는 form.resetFields()를 사용)
            // 지역/사이트 선택 필드는 유지하고, 입력 필드만 초기화하고 싶다면 Form.useForm()이 필요합니다.
            form.resetFields(); 
            
        } catch (err) {
            const apiError = axios.isAxiosError(err) ? err.response?.data?.message : '네트워크 오류';
            setError(`그룹 등록 실패: ${apiError}`);
        }
    };    


    const handleUserSiteAccessSubmit = async (values: { user_id: string; area_id: number; site_id: number }) => {
        setMessage('');
        setError('');

        // Ant Design Form이 이미 유효성 검사를 처리했으므로 별도의 if 문은 제거합니다.
        
        // payload 구조는 서버 요구 사항에 맞게 재구성합니다.
        const payload = {
            user_id: values.user_id,
            site_id: values.site_id, // site_id는 이미 Form에서 number 타입으로 처리됨
        };

        try {
            // 권한 등록 API 호출
            const response = await axios.post(`${API_BASE_URL}/api/user-site-access`, payload);
            
            setMessage(`권한 등록 성공! 사용자: ${response.data.user_id}, 사이트 ID: ${response.data.site_id}`);
            
            // 폼 초기화: 권한 등록 후 사용자 선택 필드와 사이트 선택 필드를 초기화합니다.
            // 이때 form.resetFields()를 사용합니다.
            form.resetFields(['user_id', 'site_id']); 
            
            // 추가적으로 지역 선택 상태와 연동 상태도 초기화
            setSelectedAreaId(null);
            setSites([]);
            
        } catch (err) {
            const apiError = axios.isAxiosError(err) ? err.response?.data?.message : '네트워크 오류';
            setError(`권한 등록 실패: ${apiError}`);
        }
    };    

    // 선택 상태 초기화 함수
    const resetSelections = () => {
        setSelectedAreaId(null);
        setSelectedSiteId(null);
        // Note: sites 목록은 selectedAreaId가 null로 설정되면 
        // useEffect를 통해 자동으로 비워집니다.
    };


    // 탭 변경 핸들러
    const handleTabChange = (key: TabKey) => {
        if (activeTab === key) return; // 같은 탭을 클릭했을 경우 무시
        
        setActiveTab(key);
        setMessage('');
        setError('');
        
        // 🔥 탭을 이동할 때 선택 상태 초기화
        resetSelections();
    };


    // 유저 정보 등록 신청 핸들러
    const handleUserSubmit = async (values: any) => {
        console.log('폼 제출 성공! 모든 입력값이 유효합니다:', values);
        setMessage('');
        setError('');
        try {
            // ⚠️ 중요: 서버에서는 이 values.pw (평문 비밀번호)를 받아서 bcrypt로 해시해야 합니다.
            console.log('폼 데이터 전송:', values);

            // ③ POST 요청 실행
            const response = await axios.post(`${API_BASE_URL}/api/user`, values);
            
            // ④ 성공 응답 처리
            console.log('유저 등록 성공:', response.data);
            //alert('사용자가 성공적으로 등록되었습니다!');
            setMessage('사용자가 성공적으로 등록되었습니다!');
            
            // 폼 초기화 (선택 사항)
            form.resetFields(); 

        } catch (error) {
            // ⑤ 에러 처리 (예: 서버에서 ConflictException 발생 시)
            console.error('유저 등록 실패:', error);
            
            // Axios 에러 처리
            if (axios.isAxiosError(error) && error.response) {
                //alert(`등록 실패: ${error.response.data.message || '서버 오류 발생'}`);
                setError('등록 실패:'+ error.response.data.message);
            } else {
                //alert('알 수 없는 오류가 발생했습니다.');
                setError('알 수 없는 오류가 발생했습니다.');
            }
        }
    };

    // 유효성 검사 실패 시 실행될 함수 (선택 사항)
    const onFinishFailed = (errorInfo: any) => {
        console.log('폼 유효성 검사 실패:', errorInfo);
    };

    const showNoSiteMessage = selectedAreaId !== null && sites.length === 0;

    const handleReset = () => {
        // 1. Ant Design Form 필드 초기화
        // 초기화할 필드 이름을 모두 넣어줍니다. (name="area_id", name="siteName", name="location")
        form.resetFields();

        // 2. 외부 useState 상태 초기화 (필수)
        // Select 컴포넌트의 연동 로직을 제어하는 상태를 초기화합니다.
        setSelectedAreaId(null);
        setSites([]); 
          
        // 추가로 성공/에러 메시지도 초기화합니다.
        setMessage('');
        setError('');
    };

    // --- 4. 탭 컨텐츠 렌더링 함수 ---

    const renderContent = () => {
        switch (activeTab) {
            case 'USER':
                return (
                    <Form
                        form={form}
                        name="wrap"
                        labelCol={{ flex: '110px' }}
                        labelAlign="left"
                        labelWrap
                        wrapperCol={{ flex: 1 }}
                        colon={false}
                        style={{ maxWidth: 600 }}
                        onFinish={handleUserSubmit}
                        onFinishFailed={onFinishFailed} // 폼 제출 실패 시 호출
                    >
                        <Form.Item label="ID" name="id" rules={[{ required: true, message:'ID를 입력하세요.' }]}>
                            <Input 
                                name="id"
                            />
                        </Form.Item>

                        <Form.Item label="이름" name="name" rules={[{ required: true, message:'이름을 입력하세요.' }]}>
                            <Input 
                                name="name"
                            />
                        </Form.Item>

                        <Form.Item label="암호" name="pw" rules={[{ required: true, message:'암호를 입력하세요.' }]}>
                            <Input 
                                name="pw"
                            />
                        </Form.Item>

                        <br></br><br></br>
                        {/* 버튼 */}
                        <Form.Item wrapperCol={{ offset: 5, span: 18 }}>
                            <Button type="primary" htmlType="submit">
                                유저 등록
                            </Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button 
                                type="default" 
                                onClick={handleReset} // 💡 초기화 함수 연결
                            >
                                초기화
                            </Button>
                        </Form.Item>                        
                    </Form>
                );

            case 'AREA':
                return (

                     <Form
                        form={form}
                        name="wrap"
                        labelCol={{ flex: '110px' }}
                        labelAlign="left"
                        labelWrap
                        wrapperCol={{ flex: 1 }}
                        colon={false}
                        style={{ maxWidth: 600 }}
                        onFinish={handleRegionSubmit} 
                        onFinishFailed={onFinishFailed} 
                    >
                        <Form.Item 
                            label="지역 이름" 
                            name="areaName" 
                            rules={[
                                { 
                                    required: true, 
                                    message: '지역 이름을 입력해 주세요.'
                                }
                            ]}
                        >
                            <Input 
                                placeholder="예: 서울"
                            />
                        </Form.Item>

                        <br></br><br></br>
                        {/* 버튼 */}
                        <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
                            <Button type="primary" htmlType="submit">
                                지역 등록
                            </Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button 
                                type="default" 
                                onClick={handleReset} // 💡 초기화 함수 연결
                            >
                                초기화
                            </Button>
                        </Form.Item>                        
                    </Form>
                );

            case 'SITE':
                return (
                    <Form
                        form={form}
                        name="site_registration"
                        labelCol={{ span: 6 }} // 레이블 너비 조정
                        wrapperCol={{ span: 18 }} // 입력 필드 너비 조정
                        layout="horizontal"
                        colon={false}
                        style={{ maxWidth: 600 }}
                        onFinish={handleSiteSubmit}
                        onFinishFailed={onFinishFailed}
                    >
                        {/* 1. 지역 선택 (Area Select) */}
                        <Form.Item
                            label="1. 지역 선택"
                            name="area_id" // 데이터 키 값
                            rules={[
                                { required: true, message: '지역을 선택해 주세요.' }
                            ]}
                        >
                            <Select
                                placeholder="--- 지역 선택 ---"
                                // onChange={(value) => setSelectedAreaId(Number(value))} // 지역 상태 업데이트 로직
                            >
                                {areas.map((area) => (
                                    <Option key={area.area_id} value={area.area_id}>
                                        {area.area_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* 2. 사이트 이름 입력 */}
                        <Form.Item
                            label="2. 사이트 이름"
                            name="siteName" // 데이터 키 값
                            rules={[
                                { required: true, message: '사이트 이름을 입력해 주세요.' }
                            ]}
                        >
                            <Input placeholder="사이트명 (필수)" />
                        </Form.Item>

                        {/* 3. 상세 주소 입력 (선택 사항) */}
                        <Form.Item
                            label="3. 상세 주소"
                            name="location" // 데이터 키 값
                        >
                            <Input placeholder="상세 주소" />
                        </Form.Item>
                        
                        <br></br><br></br>
                        {/* 버튼 */}
                        <Form.Item wrapperCol={{ offset: 5, span: 18 }}>
                            <Button type="primary" htmlType="submit">
                                사이트 등록
                            </Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button 
                                type="default" 
                                onClick={handleReset} // 💡 초기화 함수 연결
                            >
                                초기화
                            </Button>
                        </Form.Item>
                    </Form>
                );

            case 'GROUP':             
                return (
                    <Form
                        form={form} // form 인스턴스 연결
                        name="group_registration"
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        layout="horizontal"
                        colon={false}
                        style={{ maxWidth: 600 }}
                        onFinish={handleGroupSubmit}
                        onFinishFailed={onFinishFailed}
                    >
                        {/* 1단계: 지역 선택 (Area Select) */}
                        <Form.Item
                            label="1. 지역 선택"
                            name="area_id"
                            rules={[{ required: true, message: '지역을 선택해 주세요.' }]}
                        >
                            <Select
                                placeholder="--- 지역 선택 ---"
                                onChange={handleAreaChange} // 지역 선택 시 사이트 목록 업데이트 로직 호출
                            >
                                <Option value="">--- 지역 선택 ---</Option>
                                {areas.map((area) => (
                                    <option key={area.area_id} value={area.area_id}>
                                        {area.area_name}
                                    </option>
                                ))}                                
                            </Select>
                        </Form.Item>

                        {/* 2단계: 사이트 선택 (Site Select) */}
                        <Form.Item
                            label="2. 사이트 선택"
                            name="site_id"
                            rules={[{ required: true, message: '사이트를 선택해 주세요.' }]}
                            // 선택된 지역이 없거나 사이트가 없으면 disabled 처리
                            hasFeedback={showNoSiteMessage} 
                            help={showNoSiteMessage && <span style={{ color: 'orange' }}>선택된 지역에 사이트가 없습니다.</span>}
                        >
                            <Select
                                placeholder="--- 사이트 선택 ---"
                                disabled={!selectedAreaId || sites.length === 0}
                            >
                                <Option value="">--- 사이트 선택 ---</Option>
                                {sites.map((site) => (
                                    <Option key={site.site_id} value={site.site_id}>
                                        {site.site_name} (ID: {site.site_id})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* 3. 그룹 ID 입력 */}
                        {/* <Form.Item
                            label="3. 그룹 ID (숫자)"
                            name="groupId"
                            rules={[
                                { required: true, message: '그룹 ID를 입력해 주세요.' },
                                { pattern: /^\d+$/, message: '그룹 ID는 숫자만 가능합니다.' }
                            ]}
                        >
                            <Input placeholder="예: 1, 2 (필수)" type="number" />
                        </Form.Item> */}

                        {/* 4. 그룹 이름 입력 */}
                        <Form.Item
                            label="3. 그룹 이름"
                            name="groupName"
                            rules={[{ required: true, message: '그룹 이름을 입력해 주세요.' }]}
                        >
                            <Input placeholder="그룹 이름 (필수)" />
                        </Form.Item>
                        
                        <br></br><br></br>
                        {/* 버튼 */}
                        <Form.Item wrapperCol={{ offset: 5, span: 18 }}>
                            <Button type="primary" htmlType="submit">
                                그룹 등록
                            </Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button 
                                type="default" 
                                onClick={handleReset} // 💡 초기화 함수 연결
                            >
                                초기화
                            </Button>
                        </Form.Item>

                    </Form>
                );

            case 'ACCESS':              
                return (
                    <Form
                        form={form}
                        name="user_site_access_registration"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        layout="horizontal"
                        colon={false}
                        style={{ maxWidth: 600 }}
                        onFinish={handleUserSiteAccessSubmit}
                        onFinishFailed={onFinishFailed}
                    >
                        {/* 1. 사용자 선택 */}
                        <Form.Item
                            label="1. 사용자 (ID)"
                            name="user_id" // 서버가 요구하는 키로 변경 (예: user_id)
                            rules={[{ required: true, message: '사용자를 선택해 주세요.' }]}
                        >
                            <Select placeholder="--- 사용자 선택 ---">
                                {users.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name} (ID: {user.id})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* 2. 지역 선택 */}
                        <Form.Item
                            label="2. 지역 선택"
                            name="area_id"
                            rules={[{ required: true, message: '지역을 선택해 주세요.' }]}
                        >
                            <Select 
                                placeholder="--- 지역 선택 ---"
                                onChange={handleAreaChange} // 💡 사이트 로딩 트리거
                            >
                                <Option value="">--- 지역 선택 ---</Option>
                                {areas.map((site) => (
                                    <Option key={site.area_id} value={site.area_id}>
                                        {site.area_name} (ID: {site.area_id})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* 3. 사이트 선택 */}
                        <Form.Item
                            label="3. 사이트 선택"
                            name="site_id"
                            rules={[{ required: true, message: '사이트를 선택해 주세요.' }]}
                            hasFeedback={showNoSiteMessage} 
                            help={showNoSiteMessage && <span style={{ color: 'orange' }}>선택된 지역에 사이트가 없습니다.</span>}
                        >
                            <Select
                                placeholder="--- 사이트 선택 ---"
                                disabled={!selectedAreaId || sites.length === 0}
                            >
                                <Option value="">--- 사이트 선택 ---</Option>
                                {sites.map((site) => (
                                    <Option key={site.site_id} value={site.site_id}>
                                        {site.site_name} (ID: {site.site_id})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        
                        <br></br><br></br>
                        {/* 버튼 */}
                        <Form.Item wrapperCol={{ offset: 5, span: 18 }}>
                            <Button type="primary" htmlType="submit">
                                권한 등록
                            </Button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <Button 
                                type="default" 
                                onClick={handleReset} // 💡 초기화 함수 연결
                            >
                                초기화
                            </Button>
                        </Form.Item>                        
                    </Form>
                );

            default:
                return null;
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto', border: '1px solid #ccc', borderRadius: '8px' }}>
            <h1 style={{ textAlign: 'center' }}>통합 등록 관리</h1>
            
            <br></br><br></br>
            <center>
                <button
                    onClick={handleMainPage}
                    className={`btn btn-lg btn-success`}
                >
                    메인 페이지 이동 🚀
                </button>
            </center>
            <br></br><br></br>

            {/* 탭 네비게이션 */}
            <div style={{ 
                display: 'flex', 
                marginBottom: '20px', 
                borderBottom: '2px solid #f0eeeeff' 
            }}>
                {Object.entries(tabs).map(([key, label]) => (
                    <button
                        key={key}
                        // onClick={() => handleTabChange(key as TabKey)}
                        onClick={() => {handleTabChange(key as TabKey); handleReset();} }
                        style={{
                            flex: 1,
                            padding: '10px 15px',
                            cursor: 'pointer',
                            border: 'none',
                            fontSize: '14px',
                            backgroundColor: activeTab === key ? '#007bff' : '#f8f9fa',
                            color: activeTab === key ? 'white' : 'black',
                            borderTopLeftRadius: key === 'AREA' ? '5px' : '0',
                            borderTopRightRadius: key === 'ACCESS' ? '5px' : '0',
                            fontWeight: activeTab === key ? 'bold' : 'normal',
                            transition: 'background-color 0.3s'
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* 에러 및 메시지 영역 */}
            {error && <p style={{ color: 'red', marginBottom: '15px' }}>**오류:** <br></br> {error}</p>}
            {message && <p style={{ color: 'green', marginBottom: '15px' }}>**성공:** <br></br> {message}</p>}

            {/* 탭 컨텐츠 */}
            <div style={{ border: '1px solid #dfdedeff', padding: '20px', borderRadius: '5px' }}>
                <h2>{tabs[activeTab]}</h2>
                <br></br>
                {renderContent()}
            </div>
            
        </div>
    );
};

export default Register;